// Copyright (c) Docugami, Inc. All rights reserved.

import { FetchState } from '../shared_types'
import { BuildState } from '../../networking/api';
import { BuildType } from 'networking/BuildType';
import { OrganizationAccount, OrganizationState } from 'networking/OrganizationApi';

export interface OrganizationReduxState {
    id: string;
    name: string;
    state: OrganizationState;
    initializing: boolean;
    organizationAdmin: string;
}

export interface CreateOrganizationState {
    waiting: boolean;
    creating: boolean;
    created: boolean;
    unknown: boolean;
    statusUrl: string;
}

export interface OrganizationListState {
    fetchState: FetchState;
    indexed: { [id: string]: OrganizationReduxState };
    allIds: string[];
}

export interface PipelineBuildRequest {
    id: string;
    clusterId: string;
    buildType: BuildType;
}

export interface PipelineBuildState {
    state: BuildState;
    buildRequest: PipelineBuildRequest;
}

export interface PipelineBuildsState {
    fetchState: FetchState;
    activeBuilds: PipelineBuildState[];
}

export interface OrganizationSignalR {
    fetchState: FetchState;
}

export interface OrganizationsState {
    creationState: CreateOrganizationState;
    currentOrganization: OrganizationReduxState;
    organizationList: OrganizationListState;
    createOrganizationPolling: boolean;
    pipelineBuilds: PipelineBuildsState;
    signalR: OrganizationSignalR;
    userList: string[] | null;
    organizationAccount: OrganizationAccount | undefined;
}

export const SET_ORGANIZATION_LIST = "SET_ORGANIZATION_LIST";
export const SET_CURRENT_ORGANIZATION = "SET_CURRENT_ORGANIZATION";
export const CREATE_ORGANIZATION = "CREATE_ORGANIZATION";
export const SET_CREATE_ORGANIZATION_POLLING = "SET_CREATE_ORGANIZATION_POLLING";
export const ADD_ONBOARD_FILES_BUILD = "ADD_ONBOARD_FILES_BUILD";
export const REMOVE_ONBOARD_FILES_BUILD = "REMOVE_ONBOARD_FILES_BUILD";
export const UPDATE_ONBOARD_FILES_BUILD = "UPDATE_ONBOARD_FILES_BUILD";
export const SET_ONBOARD_FILES_BUILDS_STATE = "SET_ONBOARD_FILES_BUILDS_STATE";
export const ADD_PIPELINE_BUILD = "ADD_CLUSTER_BUILD";
export const REMOVE_PIPELINE_BUILD = "REMOVE_CLUSTER_BUILD";
export const SET_PIPELINE_BUILDS_STATE = "SET_CLUSTER_BUILDS_STATE";
export const UPDATE_ORGANIZATION = "UPDATE_ORGANIZATION";
export const SET_USER_LIST = "SET_USER_LIST";
export const SET_ORGANIZATION_ACCOUNT = "SET_ORGANIZATION_ACCOUNT";

interface SetOrganizationList {
    type: typeof SET_ORGANIZATION_LIST;
    payload: OrganizationListState;
}

interface SetCurrentOrganization {
    type: typeof SET_CURRENT_ORGANIZATION;
    payload: OrganizationReduxState;
}

interface CreateOrganization {
    type: typeof CREATE_ORGANIZATION;
    payload: CreateOrganizationState;
}

interface SetCreateOrganizationPolling {
    type: typeof SET_CREATE_ORGANIZATION_POLLING;
    payload: boolean;
}

interface AddPipelineBuild {
    type: typeof ADD_PIPELINE_BUILD;
    payload: PipelineBuildState;
}

interface RemovePipelineBuild {
    type: typeof REMOVE_PIPELINE_BUILD;
    payload: PipelineBuildState;
}

interface SetPipelineBuildsState {
    type: typeof SET_PIPELINE_BUILDS_STATE;
    payload: PipelineBuildsState;
}

interface UpdateOrganization {
    type: typeof UPDATE_ORGANIZATION;
    payload: OrganizationReduxState;
}

interface GetUserList {
    type: typeof SET_USER_LIST;
    payload: string[];
}

interface SetOrganizationAccount {
    type: typeof SET_ORGANIZATION_ACCOUNT;
    payload: OrganizationAccount;
}

export type OrganizationActionTypes =
    SetOrganizationList |
    SetCurrentOrganization |
    CreateOrganization |
    SetCreateOrganizationPolling |
    AddPipelineBuild |
    RemovePipelineBuild |
    SetPipelineBuildsState |
    UpdateOrganization |
    GetUserList |
    SetOrganizationAccount;
